/**
 * Helper functions for Price Alerts UI components
 * Extracted common patterns to reduce duplication
 */

// ============================================================================
// PRICE DISPLAY HELPERS
// ============================================================================

/**
 * Extract currency and volume unit codes from a value object or fallback to alert level
 * @param {object} valueObj - Value object with potential Currency/VolumeUnit nested objects
 * @param {object} fallback - Fallback object with currencyCode/volumeUnitCode properties
 * @returns {object} Object with currency and volumeUnit codes
 */
export function extractUnits(valueObj, fallback = {}) {
  const currency = valueObj && valueObj.Currency 
    ? valueObj.Currency.code 
    : (fallback.currencyCode || '');
  
  const volumeUnit = valueObj && valueObj.VolumeUnit 
    ? valueObj.VolumeUnit.code 
    : (fallback.volumeUnitCode || '');
  
  return { currency, volumeUnit };
}

/**
 * Format a price with currency and volume unit
 * @param {number|string} price - Price value to format
 * @param {string} currency - Currency code
 * @param {string} volumeUnit - Volume unit code
 * @param {number} decimals - Number of decimal places (default: 5)
 * @returns {string} Formatted price string like "1.23456 USD/US gal"
 */
export function formatPrice(price, currency, volumeUnit, decimals = 5) {
  if (!price || price === 'N/A') return 'N/A';
  const formattedPrice = parseFloat(price).toFixed(decimals);
  return `${formattedPrice} ${currency}/${volumeUnit}`;
}

/**
 * Format price with automatic unit extraction
 * @param {object} valueObj - Value object containing price and potentially Currency/VolumeUnit
 * @param {object} fallback - Fallback object with currencyCode/volumeUnitCode
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted price string
 */
export function formatPriceWithUnits(valueObj, fallback = {}, decimals = 5) {
  if (!valueObj || !valueObj.value) return 'N/A';
  
  const { currency, volumeUnit } = extractUnits(valueObj, fallback);
  return formatPrice(valueObj.value, currency, volumeUnit, decimals);
}

// ============================================================================
// ENTITY RESOLUTION HELPERS (for denormalization)
// ============================================================================

/**
 * Resolve an entity reference by ID from normalized store
 * @param {any} idOrObject - Entity ID or already resolved object
 * @param {string} entityType - Type of entity (e.g., 'suppliers', 'values')
 * @param {object} entities - Immutable Map of all entities from store
 * @returns {object|null} Resolved entity as plain object, or null if not found
 */
export function resolveEntity(idOrObject, entityType, entities) {
  // Already an object (not an ID reference)
  if (typeof idOrObject === 'object' && idOrObject !== null) {
    return idOrObject;
  }
  
  // It's an ID, try to resolve it
  if (idOrObject && entities) {
    const entityMap = entities.get(entityType);
    if (entityMap) {
      const entity = entityMap.get(String(idOrObject));
      return entity ? entity.toJS() : null;
    }
  }
  
  return null;
}

/**
 * Denormalize a price alert by resolving all entity references
 * @param {object} alert - Alert object with potential entity ID references
 * @param {object} entities - Immutable Map of all entities from store
 * @returns {object} Denormalized alert with all entities resolved
 */
export function denormalizeAlert(alert, entities) {
  if (!alert) return null;
  
  const denormalized = { ...alert };
  
  // Resolve Supplier
  if (denormalized.supplierId || typeof denormalized.Supplier === 'number') {
    const supplierId = denormalized.supplierId || denormalized.Supplier;
    denormalized.Supplier = resolveEntity(supplierId, 'suppliers', entities);
  }
  
  // Resolve value
  if (denormalized.valueId || typeof denormalized.value === 'number') {
    const valueId = denormalized.valueId || denormalized.value;
    denormalized.value = resolveEntity(valueId, 'values', entities);
  }
  
  // Resolve previousValue
  if (denormalized.previousValueId || typeof denormalized.previousValue === 'number') {
    const prevValueId = denormalized.previousValueId || denormalized.previousValue;
    denormalized.previousValue = resolveEntity(prevValueId, 'values', entities);
  }
  
  // Resolve Airport
  if (denormalized.airportIcao || typeof denormalized.Airport === 'string') {
    const airportId = denormalized.airportIcao || denormalized.Airport;
    denormalized.Airport = resolveEntity(airportId, 'airports', entities);
  }
  
  // Resolve resolver (user)
  if (denormalized.resolvedBy || typeof denormalized.resolver === 'number') {
    const resolverId = denormalized.resolvedBy || denormalized.resolver;
    denormalized.resolver = resolveEntity(resolverId, 'users', entities);
  }
  
  // Resolve Contract
  if (denormalized.contractId || typeof denormalized.Contract === 'number') {
    const contractId = denormalized.contractId || denormalized.Contract;
    denormalized.Contract = resolveEntity(contractId, 'contracts', entities);
  }
  
  // Resolve Platt
  if (denormalized.plattId || typeof denormalized.Platt === 'number') {
    const plattId = denormalized.plattId || denormalized.Platt;
    denormalized.Platt = resolveEntity(plattId, 'platts', entities);
  }
  
  return denormalized;
}

// ============================================================================
// MODAL HELPERS
// ============================================================================

/**
 * Create a modal state handler with common patterns
 * @param {object} component - React component instance (this)
 * @param {string} modalId - Modal ID to watch
 * @param {function} stateUpdater - Optional function to transform payload before setting state
 * @returns {function} Handler function for modal changes
 */
export function createModalChangeHandler(component, modalId, stateUpdater = null) {
  return function(ModalsStore) {
    if (ModalsStore.isModalVisible(modalId)) {
      const payload = ModalsStore.getPayload(modalId);
      const alertData = payload && payload.toJS ? payload.toJS() : payload;
      
      if (stateUpdater) {
        component.setState(stateUpdater(alertData));
      } else {
        component.setState({ alertData: alertData || {} });
      }
    }
  };
}

/**
 * Refresh price alerts list with current filters from location
 * @param {object} props - Component props containing location
 */
export function refreshAlertsWithFilters(props) {
  const filters = props.location && props.location.query ? props.location.query : {};
  const PriceAlertsAction = require('../../actions').PriceAlertsAction;
  PriceAlertsAction.fetchAll(filters);
}

/**
 * Convert modal payload to plain object (handles Immutable Maps)
 * @param {any} payload - Modal payload (could be Immutable Map or plain object)
 * @returns {object} Plain JS object
 */
export function convertModalPayload(payload) {
  return payload && payload.toJS ? payload.toJS() : payload;
}

/**
 * Get location display text for alert (Platt for private, Airport for public)
 * @param {object} alertData - Alert data object
 * @returns {string} Formatted location string
 */
export function getAlertLocation(alertData) {
  if (!alertData) return 'N/A';
  
  if (!alertData.isPublic) {
    // Private contract - show Platt
    if (alertData.Platt && (alertData.Platt.code || alertData.Platt.name)) {
      return [alertData.Platt.code, alertData.Platt.name].filter(Boolean).join(' - ');
    }
    return 'N/A';
  } else {
    // Public contract - show Airport
    return alertData.airportIcao || 'N/A';
  }
}

/**
 * Get location label for alert (Platt or Airport)
 * @param {object} alertData - Alert data object
 * @returns {string} Location label ('Platt:' or 'Airport:')
 */
export function getAlertLocationLabel(alertData) {
  return alertData && !alertData.isPublic ? 'Platt:' : 'Airport:';
}

/**
 * Format percentage change with color class
 * @param {number} percentageChange - Percentage change value
 * @returns {object} Object with {text, className}
 */
export function formatPercentageChange(percentageChange) {
  const value = percentageChange || 0;
  const className = value > 0 ? 'text-danger' : 'text-success';
  const text = `${value > 0 ? '+' : ''}${parseFloat(value).toFixed(2)}%`;
  
  return { text, className };
}
